// Pure helpers for the receiving-settings API (contract §4.6). No React, no axios.

export const LAUNCH_FLAG_KEYS = ['enabled', 'show_promises', 'express_enabled']

export const SETTINGS_KEYS = [
  'dock_count',
  'units_per_door_franja',
  'daily_processing_man_hours',
  'express_buffer_door_franjas',
  'no_show_grace_minutes',
  'reschedule_cutoff_hours',
  'checkin_units_per_hour_default',
]

export const FRANJA_FIELDS = ['label', 'weekdays', 'starts_at', 'ends_at', 'active']

// History snapshots carry seconds ("08:00:00"); the live payload and the PUT body use "HH:MM".
export const normalizeTime = value => {
  if (value == null || value === '') return null
  return String(value).slice(0, 5)
}

// 400 uses the flat {error, code} envelope; 409/422 use the nested {error: {message, code, context}} one.
export const parseReceivingError = error => {
  const status = error?.response?.status ?? null
  const data = error?.response?.data
  if (!data) return { status, code: null, message: null, context: {}, flat: false }
  if (data.error && typeof data.error === 'object') {
    return { status, code: data.error.code || null, message: data.error.message || null, context: data.error.context || {}, flat: false }
  }
  return { status, code: data.code || null, message: typeof data.error === 'string' ? data.error : null, context: {}, flat: true }
}

export const sameWeekdays = (a, b) => {
  const sa = [...(a || [])].map(Number).sort((x, y) => x - y)
  const sb = [...(b || [])].map(Number).sort((x, y) => x - y)
  return sa.length === sb.length && sa.every((v, i) => v === sb[i])
}

// A partially-typed number ("-", "1e") arrives here as a string that Number() turns into NaN,
// and JSON.stringify(NaN) is `null` — which would put on the wire the very null the
// receiving-settings contract forbids. Folding NaN to null omits the key instead, matching
// what SettingsCard's own num() already does for the validation path.
const numOrNull = v => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export const franjaFieldChanged = (field, before, after) => {
  if (field === 'weekdays') return !sameWeekdays(before?.weekdays, after?.weekdays)
  if (field === 'starts_at' || field === 'ends_at') return normalizeTime(before?.[field]) !== normalizeTime(after?.[field])
  if (field === 'active') return Boolean(before?.active) !== Boolean(after?.active)
  return (before?.[field] ?? null) !== (after?.[field] ?? null)
}

const franjaEntry = (before, after) => {
  if (!before) {
    // Create-shaped: no id, no active (an omitted active asks for a live franja).
    const entry = { label: after.label }
    const weekdays = [...(after.weekdays || [])].map(Number)
    if (weekdays.length > 0) entry.weekdays = weekdays
    const startsAt = normalizeTime(after.starts_at)
    if (startsAt !== null) entry.starts_at = startsAt
    const endsAt = normalizeTime(after.ends_at)
    if (endsAt !== null) entry.ends_at = endsAt
    return entry
  }
  const entry = { id: before.id }
  FRANJA_FIELDS.forEach(field => {
    if (!franjaFieldChanged(field, before, after)) return
    if (field === 'weekdays') entry.weekdays = [...(after.weekdays || [])].map(Number)
    else if (field === 'starts_at' || field === 'ends_at') {
      const normalized = normalizeTime(after[field])
      if (normalized !== null) entry[field] = normalized
    } else entry[field] = after[field]
  })
  return Object.keys(entry).length > 1 ? entry : null
}

export const toPutBody = (seed, draft, reason) => {
  const body = { reason }

  const seedSettings = seed?.settings || null
  const draftSettings = draft?.settings || {}
  const settings = {}
  SETTINGS_KEYS.forEach(key => {
    const after = numOrNull(draftSettings[key])
    if (after === null) return
    const before = seedSettings ? numOrNull(seedSettings[key]) : null
    if (before === null || before !== after) settings[key] = after
  })
  if (Object.keys(settings).length > 0) body.settings = settings

  const byId = new Map((seed?.franjas || []).map(f => [f.id, f]))
  const franjas = (draft?.franjas || []).map(f => franjaEntry(f.id != null ? byId.get(f.id) : null, f)).filter(Boolean)
  if (franjas.length > 0) body.franjas = franjas

  return body
}

const settingValue = (settings, key) => (settings ? numOrNull(settings[key]) : null)

const franjaKey = f => (f.id != null ? `id:${f.id}` : `label:${f.label}`)

const displayValue = (field, franja) => {
  if (field === 'starts_at' || field === 'ends_at') return normalizeTime(franja?.[field])
  if (field === 'weekdays') return [...(franja?.weekdays || [])].map(Number).sort((a, b) => a - b)
  return franja?.[field] ?? null
}

export const diffConfigs = (before, after) => {
  const settings = []
  SETTINGS_KEYS.forEach(key => {
    const from = settingValue(before?.settings, key)
    const to = settingValue(after?.settings, key)
    if (from !== to) settings.push({ key, from, to })
  })

  // Build beforeMap, excluding labels that appear more than once (to prevent false diffs).
  // Franjas are matched by id, falling back to label for id-less franjas. If multiple
  // before-franjas share the same label, none of them match the fallback key, so any
  // after-franja with that label falls into 'added'. This is incomplete but avoids
  // fabricating field changes by diffing an after-franja against the wrong before-entry.
  const beforeFranjas = before?.franjas || []
  const labelCounts = new Map()
  beforeFranjas.forEach(f => {
    if (f.id == null) {
      const label = f.label
      labelCounts.set(label, (labelCounts.get(label) || 0) + 1)
    }
  })
  const beforeMap = new Map()
  beforeFranjas.forEach(f => {
    const key = franjaKey(f)
    if (f.id != null || labelCounts.get(f.label) === 1) {
      beforeMap.set(key, f)
    }
  })

  const changed = [],
    added = [],
    retired = [],
    reactivated = []
  // Receiving-settings API never deletes franjas—only retires them via active:false.
  // So every before-franja has an after counterpart, making this one-sided walk safe.
  ;(after?.franjas || []).forEach(f => {
    const prev = beforeMap.get(franjaKey(f))
    if (!prev) {
      added.push(f)
      return
    }
    const wasActive = Boolean(prev.active),
      isActive = Boolean(f.active)
    if (wasActive && !isActive) retired.push(f)
    else if (!wasActive && isActive) reactivated.push(f)
    // A status flip and field edits can ship in the SAME save (retire + rename). The pill above
    // records the flip; the field diff below still has to run, or the rename silently vanishes
    // from the audit history. `active` is deliberately absent from the field list, so a
    // status-only change still produces no `changed` entry.
    const fields = ['label', 'weekdays', 'starts_at', 'ends_at']
      .filter(field => franjaFieldChanged(field, prev, f))
      .map(field => ({ key: field, from: displayValue(field, prev), to: displayValue(field, f) }))
    if (fields.length > 0) changed.push({ id: f.id ?? null, label: f.label, fields })
  })

  const isEmpty = settings.length === 0 && changed.length === 0 && added.length === 0 && retired.length === 0 && reactivated.length === 0
  return { settings, franjas: { changed, added, retired, reactivated }, isEmpty }
}

const stripLaunchFlags = settings => {
  if (!settings) return null
  const out = {}
  Object.keys(settings).forEach(k => {
    if (!LAUNCH_FLAG_KEYS.includes(k)) out[k] = settings[k]
  })
  return out
}

export const canRevert = row => Boolean(row?.before_config?.settings)

// The revert is "PUT the before snapshot": diff after → before with toPutBody, then retire any
// franja the reverted save created (it cannot be deleted through the API).
// Invariant: The API never deletes franjas—only retires them via active:false. canRevert restricts
// this to rows with a real before_config.settings, which are rows with a real after_config. Therefore,
// every before-franja id is present in after_config.franjas (possibly retired as active:false). This
// keeps toPutBody's id-matching on the restore path instead of the create path. If the invariant
// breaks, an id-less create-shaped entry would duplicate the franja instead of restoring it.
export const buildRevertBody = row => {
  if (!canRevert(row)) return null
  const seed = { settings: stripLaunchFlags(row.after_config?.settings), franjas: row.after_config?.franjas || [] }
  const beforeFranjas = row.before_config?.franjas || []
  const beforeIds = new Set(beforeFranjas.map(f => f.id))
  const draftFranjas = [...beforeFranjas, ...seed.franjas.filter(f => !beforeIds.has(f.id)).map(f => ({ ...f, active: false }))]
  const draft = { settings: stripLaunchFlags(row.before_config.settings), franjas: draftFranjas }
  return toPutBody(seed, draft, `Revertir cambio #${row.id}`)
}
